// AestheticFluid shader: two-layer approach similar to AestheticFluidBg
// 1) Splat-like blurred dots mixed over a base gradient using 6 dyes
// 2) Post wavy UV warp for liquid motion

const frag = `
precision mediump float;

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_speed;
uniform float u_seed;

uniform float u_radius_inner; // 0.0 - 0.5
uniform float u_radius_outer; // 0.0 - 1.0
uniform float u_magnitude;    // 0.0 - 0.4

uniform vec3 u_color_0;
uniform vec3 u_color_1;
uniform vec3 u_color_2;
uniform vec3 u_color_3;
uniform vec3 u_color_4;
uniform vec3 u_color_5;

varying vec2 vUv;

float rand(vec2 n) { return fract(sin(dot(n, vec2(12.9898, 78.233)) + u_seed) * 43758.5453); }

vec2 dyePos(int i) {
  // seeded stable positions
  float fi = float(i);
  return vec2(rand(vec2(fi, u_seed * 0.137)), rand(vec2(fi + 17.0, u_seed * 0.271)));
}

vec2 wavy(vec2 uv) {
  const float base = 15.0;
  float t = u_time * u_speed;
  float offS = sin(t + uv.y * base) * u_magnitude;
  float offT = cos(t + uv.x * base) * u_magnitude;
  return uv + vec2(offS, offT);
}

vec4 blurDot(vec3 color, vec2 st, vec2 pos, float inner, float outer) {
  float pct = distance(st, pos);
  float alpha = 1.0 - smoothstep(inner, outer, pct);
  return vec4(color, alpha);
}

vec3 colorAtIndex(int idx) {
  if (idx == 0) return u_color_0;
  if (idx == 1) return u_color_1;
  if (idx == 2) return u_color_2;
  if (idx == 3) return u_color_3;
  if (idx == 4) return u_color_4;
  return u_color_5;
}

void main() {
  vec2 st = gl_FragCoord.xy / u_resolution;
  // layer 1: base gradient blend of first three colors
  vec3 base = mix(u_color_0, u_color_1, st.x);
  base = mix(base, u_color_2, st.x * st.x - 0.04);

  // generate dyes
  vec4 dyes[6];
  for (int i = 0; i < 6; i++) {
    vec2 pos = dyePos(i);
    float inner = mix(0.05, 0.2, rand(pos + 0.31)) * u_radius_inner * 2.0 + u_radius_inner;
    float outer = inner + (mix(0.2, 0.6, rand(pos + 0.79)) * u_radius_outer + u_radius_outer);
    dyes[i] = vec4(pos, inner, outer);
  }

  // mix dots
  vec3 color = base;
  for (int i = 0; i < 6; i++) {
    vec4 d = dyes[i];
    vec4 splat = blurDot(colorAtIndex(i), st, d.xy, d.z, d.w);
    color = mix(color, splat.rgb, splat.a);
  }

  // final liquid warp
  vec2 warped = wavy(st);
  // simple feedback by slightly using warped coords in gradient for sheen
  vec3 sheen = mix(u_color_3, u_color_4, warped.y);
  color = mix(color, sheen, 0.08);

  gl_FragColor = vec4(color, 1.0);
}
`;

const shader = {
  name: 'AestheticFluid',
  shader: {
    frag,
  },
  schema: [
    {
      key: 'colors',
      type: 'color-palette',
      label: 'Colors',
      min: 2,
      max: 6,
      default: ['#36d1dc', '#5b86e5', '#ffffff', '#ffd1ff', '#a1c4fd', '#c2e9fb'],
    },
    {
      key: 'u_radius_inner',
      type: 'number',
      label: 'Inner Radius',
      min: 0.0,
      max: 0.5,
      step: 0.01,
      default: 0.1,
    },
    {
      key: 'u_radius_outer',
      type: 'number',
      label: 'Outer Radius',
      min: 0.0,
      max: 1.0,
      step: 0.01,
      default: 0.3,
    },
    {
      key: 'u_magnitude',
      type: 'number',
      label: 'Magnitude',
      min: 0.0,
      max: 0.4,
      step: 0.005,
      default: 0.15,
    },
    { key: 'u_seed', type: 'number', label: 'Seed', min: 0, max: 9999, step: 1, default: 0 },
    { key: 'u_speed', type: 'number', label: 'Speed', min: 0, max: 10, step: 0.1, default: 1 },
    { key: 'animated', type: 'boolean', label: 'Animated', default: true },
  ],
  examples: [
    'dynamic-shader(AestheticFluid, colors=#36d1dc,#5b86e5,#fff,#ffd1ff,#a1c4fd,#c2e9fb; u_radius_inner=0.1; u_radius_outer=0.3; u_magnitude=0.15; animated=false)',
    'dynamic-shader(AestheticFluid, colors=#ff9966,#ff5e62,#222,#ffe,#eef,#fef; u_radius_inner=0.08; u_radius_outer=0.25; u_magnitude=0.10; animated=false)',
  ],
};

export default shader;
